import {Box, Typography, useTheme} from '@mui/material'
import {arc, pie} from 'd3'
import type {FC, ReactElement} from 'react'
import {memo, useMemo, useRef, useState} from 'react'
import {nivoDarkColors} from '@shared/components/graphThemes/nivoDarkColors'
import {nivoLightColors} from '@shared/components/graphThemes/nivoLightColors'
import ChartTooltip from './ChartTooltip'
import ChartActionsMenu from './ChartActionsMenu'
import {CHART_VIEW_H as VIEW_H, GRAPH_MAX_HEIGHT, sanitizeChartColor} from './chartConstants'
import {resizableGraphPanelSx} from './chartStyles'
import {useResizeGrip} from '@containers/Dashboards/AgentChat/components/messages/useResizeGrip'
import type {PieChartSpec} from './types'

/** Generic donut chart (native d3 pie/arc) with a side legend + hover tooltip. */

const CX = 150
const CY = VIEW_H / 2
const R = 120
const R_INNER = 66
const LABEL_MIN_ANGLE = 0.32 // rad (~18°): below this a slice is too thin to label
const LEGEND_X = CX + R + 28 // legend column start, just right of the donut
const LEGEND_TEXT_X = 18 // swatch (12) + gap before the label text
const LEGEND_CHAR_W = 7 // ~px per char at the 12px legend font (over-estimate slightly)
const LEGEND_LABEL_MAX = 26 // labels are truncated to this many chars
const VIEW_PAD_R = 16 // right padding after the widest label

interface Props {
  spec: PieChartSpec
}

const ChatPieChart: FC<Props> = ({spec}): ReactElement | null => {
  const theme = useTheme()
  const palette = theme.palette.mode === 'dark' ? nivoDarkColors : nivoLightColors

  const panelRef = useRef<HTMLDivElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hover, setHover] = useState<{idx: number; left: number; top: number} | null>(null)

  // Content-fit viewBox width: the donut plus a legend column sized to the widest
  // (truncated) label, so the pie carries no empty band on the right -- in-chat and in the
  // PNG export, which share this viewBox. The panel opens at the standard height, its width
  // derived from this (narrower) ratio.
  const maxLabelChars = Math.min(
    LEGEND_LABEL_MAX,
    spec.data.reduce((m, d) => Math.max(m, d.label.length), 0),
  )
  const viewW = LEGEND_X + LEGEND_TEXT_X + maxLabelChars * LEGEND_CHAR_W + VIEW_PAD_R
  const pieDefaultWidth = Math.round((GRAPH_MAX_HEIGHT * viewW) / VIEW_H) + 26 // + panel chrome
  useResizeGrip(panelRef, {mode: 'aspect', aspectRatio: viewW / VIEW_H})

  const model = useMemo(() => {
    const total = spec.data.reduce((sum, d) => sum + d.value, 0) || 1
    const arcs = pie<{label: string; value: number}>()
      .value((d) => d.value)
      .sort(null)(spec.data)
    const arcGen = arc<(typeof arcs)[number]>().innerRadius(R_INNER).outerRadius(R)
    const labelArc = arc<(typeof arcs)[number]>()
      .innerRadius((R + R_INNER) / 2)
      .outerRadius((R + R_INNER) / 2)

    const slices = arcs.map((a, i) => {
      const [lx, ly] = labelArc.centroid(a)
      return {
        label: spec.data[i].label,
        value: spec.data[i].value,
        pct: (spec.data[i].value / total) * 100,
        color: sanitizeChartColor(spec.data[i].color) ?? palette[i % palette.length],
        d: arcGen(a) ?? '',
        angle: a.endAngle - a.startAngle,
        lx,
        ly,
      }
    })
    return {slices}
  }, [spec, palette])

  const onMove = (e: React.MouseEvent<Element>, idx: number): void => {
    const el = wrapRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setHover({idx, left: e.clientX - rect.left, top: e.clientY - rect.top})
  }

  if (!model.slices.length) return null
  const active = hover ? model.slices[hover.idx] : null

  return (
    <Box ref={panelRef} sx={[resizableGraphPanelSx, {width: pieDefaultWidth}]}>
      <ChartActionsMenu svgRef={svgRef} title={spec.title} />
      {spec.title ? (
        <Typography variant="subtitle2" sx={{mb: 0.5, fontWeight: 600}}>
          {spec.title}
        </Typography>
      ) : null}

      <Box
        ref={wrapRef}
        data-graph-area
        onMouseLeave={() => setHover(null)}
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${viewW} / ${VIEW_H}`,
          overflow: 'visible',
        }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${viewW} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={spec.title ?? 'pie chart'}
          style={{width: '100%', height: '100%', display: 'block', overflow: 'visible'}}
        >
          <g transform={`translate(${CX},${CY})`}>
            {model.slices.map((s, i) => (
              <g key={s.label}>
                <path
                  d={s.d}
                  fill={s.color}
                  stroke={theme.palette.background.paper}
                  strokeWidth={1.5}
                  opacity={active && hover?.idx !== i ? 0.5 : 1}
                  onMouseMove={(e) => onMove(e, i)}
                />
                {s.angle >= LABEL_MIN_ANGLE ? (
                  <text
                    x={s.lx}
                    y={s.ly}
                    textAnchor="middle"
                    dy="0.32em"
                    fontSize={11}
                    fontWeight={600}
                    fill={theme.palette.getContrastText(s.color)}
                    pointerEvents="none"
                  >
                    {Math.round(s.pct)}%
                  </text>
                ) : null}
              </g>
            ))}
          </g>

          {/* legend */}
          <g transform={`translate(${LEGEND_X}, ${CY - model.slices.length * 11})`}>
            {model.slices.map((s, i) => (
              <g key={s.label} transform={`translate(0, ${i * 22})`}>
                <rect width={12} height={12} rx={2} fill={s.color} />
                <text x={18} y={10} fontSize={12} fill={theme.palette.text.primary}>
                  {s.label.length > 26 ? `${s.label.slice(0, 25)}…` : s.label}
                </text>
              </g>
            ))}
          </g>
        </svg>

        <ChartTooltip left={hover?.left ?? 0} top={hover?.top ?? 0} visible={Boolean(active)}>
          {active ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Box sx={{width: 8, height: 8, borderRadius: '2px', bgcolor: active.color}} />
              <Typography variant="caption" sx={{color: 'text.secondary'}}>
                {active.label}: <strong>{active.value}</strong> ({active.pct.toFixed(1)}%)
              </Typography>
            </Box>
          ) : null}
        </ChartTooltip>
      </Box>
    </Box>
  )
}

// Memoised (stable spec identity) so streaming re-renders skip this chart.
export default memo(ChatPieChart)
